import { type MutateFunction, useMutation } from '@vue/apollo-composable';
import { ref } from 'vue';
import { useStore } from 'vuex';

import ShopEventBus from '@/components/shop-modules/shop-event-bus';
import type { AddItemMutation, AddItemMutationVariables, GetCartQuery } from '@/generated/graphql';
import ADD_MULTIPLE_PRODUCTS_TO_CART from '@/graphql/addMultipleProductsToCart.gql';
import ADD_PRODUCT_TO_CART from '@/graphql/addProductToCart.gql';
import GET_CART from '@/graphql/getCart.gql';
import type { SitecoreDictionary } from '@/lib/dictPlugin';
import type { FischerPlugin } from '@/lib/fischerPlugin';
import { getIsIds } from '@/utils/idsTools';

import { useDict, useFischer } from './fischerPlugin';

export type GetCartCodeEmit = (
  type: 'get-cart-code',
  resolve: (cartId: string | null) => void,
  reject: (error: any) => void,
) => void;

export type Cart = NonNullable<NonNullable<GetCartQuery['getCart']>['cart']>;
export type CartProduct = NonNullable<NonNullable<Cart['products']>[0]>;

export interface AddProductToCartParameters {
  productID: string;
  productRequestedQuantity?: number;
  productInitialQuantity?: number;
  disableNotification?: boolean;
  rethrowErrors?: boolean;
  targetCart?: string | undefined;
  disableTracking?: boolean;
  fromMemoList?: boolean;
  actionType?: string;
}

export const addingToCartKey = 'adding-to-cart';
export const getItemsFromSession = () => {
  if (IS_SSR) {
    return null;
  }
  const json = window.sessionStorage.getItem(addingToCartKey);
  const items: Record<string, any> | null = (json && JSON.parse(json)) || null;
  return items;
};

export const addProductToCartFunction = async (
  {
    productID,
    productRequestedQuantity,
    productInitialQuantity,
    disableNotification = false,
    rethrowErrors = false,
    targetCart,
    disableTracking = false,
    fromMemoList = false,
    actionType,
  }: AddProductToCartParameters,
  isAddingItemToCart: Record<string, true>,
  setIsUpdating: (value: boolean) => void,
  getCartId: () => Promise<string | null>,
  addProductToCartMutation: MutateFunction<AddItemMutation, AddItemMutationVariables>,
  fischer: FischerPlugin,
  enterErrorState: (...errors: any) => void,
  store: { dispatch: any },
  dict: SitecoreDictionary,
  retreiveCart: () => Cart | null,
  replaceCart: (cart: Cart) => void,
  notifyCartChange: () => void,
  getCurrentCartId: (noWarning?: boolean) => Promise<string | null>,
  trackAddToCart:
    | undefined
    | ((
        trackingCartId: string,
        productID: string,
        quantity: number,
        fromMemoList: boolean,
        actionType?: string,
      ) => void),
) => {
  try {
    setIsUpdating(true);
    isAddingItemToCart[productID] = true;
    const startTime = Date.now();

    const cartId = targetCart || (await getCartId());
    if (!cartId) {
      throw new Error('missing cart id');
    }

    const result = await addProductToCartMutation({
      baseSiteId: fischer.baseSiteId(),
      accessToken: fischer.user.accessToken() ? fischer.user.accessToken() : '',
      cartId,
      id: productID,
      quantity: productRequestedQuantity || productInitialQuantity,
      isIds: getIsIds(),
      userId: fischer.user.id(),
      lang: fischer.language_639_1(),
      division: fischer.user.division(),
      distributionChannel: fischer.user.distributionChannel(),
    });
    const data = result?.data;
    const errors = result?.errors;

    setIsUpdating(false);
    delete isAddingItemToCart[productID];

    if (errors) {
      if (!rethrowErrors) {
        enterErrorState(errors);
      }
      return { success: false, errors: { graphQLErrors: errors } };
    }

    const addItem = data?.addItem;
    if (addItem?.addedItems !== undefined) {
      const { addedItems, requestedItems } = addItem;
      if (addedItems !== requestedItems) {
        store.dispatch('notifications/openToastNotification', {
          name: `toast-error-${Math.random()}`,
          message: {
            value: addedItems
              ? dict.get('checkout.Cart.Could-Not-Add-All-Items', [
                  String(requestedItems || 0),
                  String(addedItems),
                ])
              : dict.get('checkout.Cart.Could-Not-Add-Items', [String(requestedItems || 0)]),
          },
          type: 'error',
          icon: 'alert-triangle',
          close: true,
          persist: true,
        });
      }

      // tell sitecore to set new cart Id
      if (
        addItem.cartId &&
        addItem.cartId !== 'current' &&
        !fischer.cartId() &&
        addItem.cartId !== fischer.cartId()
      ) {
        fischer.setCartId(addItem.cartId);
      }

      const addedAnItem = addedItems !== 0;
      if (addedAnItem) {
        if (fischer.shop.isRetailConnectCart()) {
          const cart = JSON.parse(JSON.stringify(retreiveCart()));
          if (cart) {
            // fake adding product to cart to disable add-button for RetailConnectCart-ShopType
            // to not call the long loading getCart function again
            if (cart.products) {
              cart.products.push({ articleNo: productID });
            } else {
              cart.products = [{ articleNo: productID }];
            }
            replaceCart(cart);
          } else {
            console.error('did not receive a cart');
          }
        }

        if (!disableNotification) {
          notifyCartChange();
        }

        // track event
        if (!disableTracking) {
          const id = Math.random().toString();
          const quantity = productRequestedQuantity || productInitialQuantity;
          try {
            const items = getItemsFromSession() || {};
            items[id] = [productID, quantity, fromMemoList, actionType];
            window.sessionStorage.setItem(addingToCartKey, JSON.stringify(items));
          } catch (error) {
            if (IS_SHOWROOM || APP_ENVIRONMENT === 'TEST') {
              console.error('can not store', addingToCartKey, error);
            }
          }
          const trackingCartId = await getCurrentCartId(true);
          if (trackingCartId && quantity) {
            trackAddToCart?.(trackingCartId, productID, quantity, fromMemoList, actionType);
            try {
              const items = getItemsFromSession() || {};
              delete items.id;
              if (Object.keys(items).length) {
                window.sessionStorage.setItem(addingToCartKey, JSON.stringify(items));
              } else {
                window.sessionStorage.removeItem(addingToCartKey);
              }
            } catch (error) {
              if (IS_SHOWROOM || APP_ENVIRONMENT === 'TEST') {
                console.error('can not remove', addingToCartKey, error);
              }
            }
          } else {
            console.error('missing data for eventhub', trackingCartId, quantity);
          }
        }

        return { success: addedAnItem, startTime };
      }

      console.error('could not add item:', addItem.statusCode);
      return {
        success: false,
        errors: { error: 'could not add item', statusCode: addItem.statusCode },
      };
    }

    const msg = 'an error has occurred, got no addItem or addedItems';
    if (rethrowErrors) {
      return { success: false, errors: msg };
    }
    enterErrorState(msg, data);
    return { success: false };
  } catch (errors) {
    delete isAddingItemToCart[productID];
    if (!rethrowErrors) {
      enterErrorState('error for addProductToCart', errors);
    }
    return { success: false, errors };
  }
};

export const addMultipleProductsToCartFunction = async (
  {
    products,
    createNewCart = false,
    disableNotification,
    rethrowErrors,
    noTracking,
  }: {
    products: {
      productID: string;
      productRequestedQuantity?: number;
      productInitialQuantity?: number;
    }[];
    createNewCart?: boolean;
    disableNotification?: boolean;
    rethrowErrors?: boolean;
    noTracking?: boolean;
  },
  {
    enterErrorState,
    fischer,
    getCartId,
    getCurrentCartId,
    isAddingItemToCart,
    mutate,
    notifyCartChange,
    setIsUpdating,
    trackAddMultipleToCart,
  }: {
    enterErrorState: (errorId: string, errors: any) => void;
    fischer: FischerPlugin;
    getCartId: () => Promise<string | null>;
    getCurrentCartId: () => Promise<string | null>;
    isAddingItemToCart: Record<string, true>;
    mutate: (variables: {
      baseSiteId: string;
      accessToken: string;
      userId: string;
      cartId: string;
      items: { id: string; quantity: number }[];
      isIds: boolean;
      createNewCart: boolean;
      lang: string;
      division: string | undefined;
      distributionChannel: string | undefined;
    }) => Promise<null | { data?: any }>;
    notifyCartChange: () => void;
    setIsUpdating: (value: boolean) => void;
    trackAddMultipleToCart: (
      trackingCartId: string,
      items: {
        id: string;
        quantity: number;
      }[],
    ) => any | undefined;
  },
) => {
  try {
    setIsUpdating(true);
    products.forEach(({ productID }) => {
      isAddingItemToCart[productID] = true;
    });

    const cartId = await getCartId();
    if (!cartId) {
      throw new Error('missing cart id');
    }

    const items = products.map((product) => ({
      id: product.productID,
      quantity: product.productRequestedQuantity || product.productInitialQuantity || 1,
    }));

    const result = await mutate({
      baseSiteId: fischer.baseSiteId(),
      accessToken: fischer.user.accessToken(),
      userId: fischer.user.id(),
      cartId,
      items,
      isIds: getIsIds(),
      createNewCart,
      lang: fischer.language_639_1(),
      division: fischer.user.division(),
      distributionChannel: fischer.user.distributionChannel(),
    });

    if (!noTracking) {
      const trackingCartId = await getCurrentCartId();
      if (trackingCartId) {
        trackAddMultipleToCart(trackingCartId, items);
      } else {
        console.error('Did not get cart id for eventhub');
      }
    }

    const addMultipleItems = result?.data?.addMultipleItems;
    if (!addMultipleItems) {
      throw new Error('Did not receive expected result from add multiple items');
    }

    // tell sitecore to set new cart Id
    if (
      addMultipleItems.cartId &&
      addMultipleItems.cartId !== 'current' &&
      !fischer.cartId() &&
      addMultipleItems.cartId !== fischer.cartId()
    ) {
      fischer.setCartId(addMultipleItems.cartId);
    }

    const hadSuccess = addMultipleItems.cartModifications.some(
      (modification: { statusCode?: string }) => modification.statusCode === 'success',
    );
    if (hadSuccess) {
      if (!disableNotification) {
        notifyCartChange();
      }
    }

    return addMultipleItems.cartModifications;
  } catch (errors) {
    if (!rethrowErrors) {
      enterErrorState('error for addMultipleItems', errors);
    }
    return { success: false, errors };
  } finally {
    setIsUpdating(false);
    if (products) {
      products.forEach(({ productID }) => {
        delete isAddingItemToCart[productID];
      });
    }
  }
};

export const getCurrentCartIdFunction = async (
  fischer: FischerPlugin,
  getCart: (_param?: boolean, _param2?: boolean, noWarning?: boolean) => any,
  retreiveCart: () => Cart | null,
  noWarning = false,
) => {
  await getCart(undefined, undefined, noWarning);
  const cart = retreiveCart();
  if (fischer.user.id() === 'anonymous' && cart?.guid) {
    return cart.guid as string;
  } else if (fischer.user.id() !== 'anonymous' && cart?.id) {
    return cart.id as string;
  }
  return null;
};

export const getCartIdFunction = (
  useCartCode: boolean,
  cartCode: string | null,
  emit: GetCartCodeEmit | undefined,
  fischer: FischerPlugin,
  getShoppingCart: () => Promise<string | null>,
): Promise<string | null> => {
  const salesforce = fischer.user.getSalesforce()?.cart?.code;
  if (salesforce) {
    return salesforce;
  }
  if (useCartCode) {
    if (cartCode) {
      return Promise.resolve(cartCode);
    }
    return new Promise((resolve, reject) => {
      if (emit) {
        emit('get-cart-code', resolve, reject);
      } else {
        reject('can not emit get-cart-code');
      }
    });
  }
  return getShoppingCart();
};

export const updateShoppingcartCounterFunction = (store: { dispatch: any }, amount: number) =>
  store.dispatch('cart/setPositionsCount', amount);

export const checkMfvCodesFunction = (
  errors: string[],
  setMfvCodes: (codes: string[]) => void,
  store: { dispatch: any },
  fischer: FischerPlugin,
) => {
  const mfvCodes: string[] = [];
  errors.forEach((error) => {
    if (error.startsWith('MFV-')) {
      mfvCodes.push(error);
    } else if (fischer.user.getAssistedService()) {
      store.dispatch('notifications/openToastNotification', {
        name: Math.random(),
        text: error,
        type: 'error',
        close: true,
        persist: true,
        action: {
          label: '',
        },
      });
    }
  });
  setMfvCodes(mfvCodes);
};

export const getCartFunction = async (
  showLoading: boolean = true,
  noOrderSimulateParam: boolean = false,
  noWarning: boolean = false,
  dict: SitecoreDictionary,
  fischer: FischerPlugin,
  getMfvCode: () => string[],
  logoutIfTokenInvalid: (errors: any) => Promise<void> | undefined,
  mfvCodeErrorHandler: undefined | (() => void),
  mutate: (variables: {
    baseSiteId: string;
    accessToken: string;
    userId: string;
    cartId: string;
    cacheDefeat: number;
    catalogLocale: string;
    noOrderSimulate: boolean;
    isIds: boolean;
    idToken?: string;
    lang: string;
    agent: boolean;
    division: string | undefined;
    distributionChannel: string | undefined;
  }) => Promise<null | { data?: any }>,
  setCart: (value: any) => void,
  setDeliveryAddressId: (data: string) => void,
  setHaveErrors: (value: boolean) => void,
  setIsLoading: (value: boolean) => void,
  setIsUpdating: (value: boolean) => void,
  setMfvCodes: (codes: string[]) => void,
  store: { dispatch: any },
): Promise<{ success: boolean }> => {
  if (!fischer.shop.supportsCart()) {
    console.error('Cart not supported');
    return { success: false };
  }

  if (fischer.user.id() === 'anonymous' && fischer.cartId() && !fischer.cartId().includes('-')) {
    fischer.setCartId('');
  }

  const noOrderSimulate = noOrderSimulateParam || fischer.shop.isRetailConnectCart();

  setIsUpdating(true);
  if (showLoading) {
    setIsLoading(true);
  }

  try {
    const assistedService = fischer.user.getAssistedService();
    const result = await mutate({
      baseSiteId: fischer.baseSiteId(),
      accessToken: fischer.user.accessToken(),
      userId: fischer.user.id(),
      cartId: fischer.cartId(),
      cacheDefeat: new Date().getTime(),
      catalogLocale: fischer.catalogLocale(),
      noOrderSimulate,
      isIds: getIsIds(),
      idToken: assistedService?.idToken,
      lang: fischer.language_639_1(),
      agent: Boolean(fischer.user.getAssistedService()),
      division: fischer.user.division(),
      distributionChannel: fischer.user.distributionChannel(),
    });
    const getCart = result?.data.getCart || { error: ['Got no cart in response'] };
    const { cart, errors } = getCart;
    setCart(cart);
    if (cart?.deliveryAddress?.id) {
      setDeliveryAddressId(cart.deliveryAddress.id);
    }

    if (cart?.guid && (!fischer.cartId() || fischer.cartId() === '')) {
      fischer.setCartId(getCart.cart.guid);
    }

    setIsUpdating(false);
    if (showLoading) {
      setIsLoading(false);
    }
    updateShoppingcartCounterFunction(store, cart ? cart.totalItems : 0);
    const mfvCode = getMfvCode();
    if (errors) {
      const hadMfvCode = mfvCode.length;
      checkMfvCodesFunction(errors, setMfvCodes, store, fischer);
      if (!hadMfvCode && mfvCode.length) {
        mfvCodeErrorHandler?.();
      }
    }
    if (errors?.length && errors.length !== mfvCode.length) {
      console.error(getCart.errors);
      store.dispatch('notifications/openGeneralErrorNotification', { dict });
      setHaveErrors(true);
    } else {
      setHaveErrors(false);
    }

    return { success: true };
  } catch (error) {
    if (!noWarning) {
      store.dispatch('notifications/openGeneralErrorNotification', { dict });
      setHaveErrors(true);
      console.error(error);
    }
    await logoutIfTokenInvalid(error);

    return { success: false };
  }
};

export const useShoppingcart = ({
  enterErrorState,
  useCartCode = false,
  emit,
  trackAddMultipleToCart,
  trackAddToCart,
  logoutIfTokenInvalid,
  mfvCodeErrorHandler,
}: {
  enterErrorState?: (type: string, error: any) => void;
  useCartCode?: boolean;
  emit?: GetCartCodeEmit;
  trackAddMultipleToCart?: (
    trackingCartId: string,
    items: {
      id: string;
      quantity: number;
    }[],
  ) => any | undefined;
  trackAddToCart?: (
    trackingCartId: string,
    productID: string,
    quantity: number,
    fromMemoList: boolean,
    actionType?: string,
  ) => void;
  logoutIfTokenInvalid?: (errors: any) => Promise<void> | undefined;
  mfvCodeErrorHandler?: () => void;
}) => {
  const fischer = useFischer();
  const dict = useDict();
  const store = useStore();

  const isUpdating = ref<boolean>(false);
  const isAddingItemToCart = ref<Record<string, true>>({});
  const cart = ref<Cart | null>(null);
  const mfvCode = ref<string[]>([]);
  const deliveryAddress = ref<string | null>(null);
  const haveErrors = ref(false);
  const isLoading = ref(false);
  const cartCode = ref<string | null>('');

  const { mutate: getCartMutation } = useMutation(GET_CART);
  const getCart = (showLoading?: boolean, noOrderSimulateParam?: boolean, noWarning?: boolean) =>
    getCartFunction(
      showLoading,
      noOrderSimulateParam,
      noWarning,
      dict,
      fischer,
      () => mfvCode.value,
      (errors) => logoutIfTokenInvalid?.(errors),
      () => mfvCodeErrorHandler?.(),
      (variables) => getCartMutation(variables),
      (value) => {
        cart.value = value;
      },
      (value) => (deliveryAddress.value = value),
      (value) => (haveErrors.value = value),
      (value) => (isLoading.value = value),
      (value) => (isUpdating.value = value),
      (value) => (mfvCode.value = value),
      store,
    );

  const getCurrentCartId = (noWarning = false) => {
    return getCurrentCartIdFunction(
      fischer,
      () => getCart(),
      () => cart.value,
      noWarning,
    );
  };

  const getShoppingCart = async () => {
    if (!fischer.cartId()) {
      await getCart();
    }
    return fischer.cartId();
  };

  const updateShoppingcartCounter = (amount: number) => {
    updateShoppingcartCounterFunction(store, amount);
  };

  const getCartId = () =>
    getCartIdFunction(useCartCode, cartCode.value, emit, fischer, () => getShoppingCart());

  const getCartPositionsCount = async (): Promise<number> => {
    if (IS_SSR) {
      return 0;
    }
    await store.dispatch('cart/refetchPositionsCount');
    return store.getters['cart/positionsCount'] || 0;
  };

  const notifyCartChange = async (noCartSnackBar?: boolean) => {
    try {
      const newCartPositionsCount = await getCartPositionsCount();

      if (!noCartSnackBar) {
        ShopEventBus.$emit('ProductAddToCart', true);
        ShopEventBus.$emit('ShoppingcartCounter', newCartPositionsCount);
      }

      updateShoppingcartCounter(newCartPositionsCount);
    } catch (error) {
      enterErrorState?.('an error has occurred, notifyCartChange', error);
    }
  };

  const { mutate: addProductToCartMutation } = useMutation<AddItemMutation>(ADD_PRODUCT_TO_CART, {
    errorPolicy: 'all',
  });
  const addProductToCart = async (args: AddProductToCartParameters) =>
    addProductToCartFunction(
      args,
      isAddingItemToCart.value,
      (value) => (isUpdating.value = value),
      getCartId,
      addProductToCartMutation,
      fischer,
      enterErrorState || console.error,
      store,
      dict,
      () => cart.value,
      (newCart: Cart) => (cart.value = newCart),
      notifyCartChange,
      getCurrentCartId,
      trackAddToCart,
    );

  const { mutate: addMultipleProductsToCartMutation } = useMutation(ADD_MULTIPLE_PRODUCTS_TO_CART);
  const addMultipleProductsToCart = async ({
    products,
    createNewCart = false,
    disableNotification = false,
    rethrowErrors = false,
    noTracking,
  }: Parameters<typeof addMultipleProductsToCartFunction>[0]) =>
    addMultipleProductsToCartFunction(
      {
        products,
        createNewCart,
        disableNotification,
        rethrowErrors,
        noTracking,
      },
      {
        enterErrorState: (type, errors) => enterErrorState?.(type, errors),
        fischer: fischer,
        getCartId: () => getCartId(),
        getCurrentCartId: () => getCurrentCartId(),
        isAddingItemToCart: isAddingItemToCart.value,
        mutate: (variables) => addMultipleProductsToCartMutation(variables),
        notifyCartChange,
        setIsUpdating: (value) => (isUpdating.value = value),
        trackAddMultipleToCart: (trackingId, items) => trackAddMultipleToCart?.(trackingId, items),
      },
    );

  return {
    addMultipleProductsToCart,
    addProductToCart,
    cart,
    cartCode,
    deliveryAddress,
    getCart,
    getCartPositionsCount,
    getCurrentCartId,
    haveErrors,
    isAddingItemToCart,
    isLoading,
    isUpdating,
    mfvCode,
    notifyCartChange,
    updateShoppingcartCounter,
  };
};
